// PeopleSegmentBar — the top-level switcher for the merged "People" area
// (UX Phase 4, 2026-08-08). Players, Agents and Bots used to be three
// separate destinations (two top-nav tabs + Bots buried under Settings).
// They're now one "People" place; this bar switches between the three
// segments and surfaces the two maintenance pages that previously had no
// menu entry at all (Unassigned players) or lived far away (Bulk reassign).
//
// Rendered at the very top of PlayersHub, AgentsHub and BotsHub, so it's
// consistent across all three segments. Each segment page keeps its own
// inner Requests | List tabs beneath this bar.
import { Link, useLocation } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';

type Segment = 'players' | 'agents' | 'bots';

const SEGMENTS: { key: Segment; label: string; icon: string; to: string; prefix: string }[] = [
  { key: 'players', label: 'Players', icon: 'group',         to: '/people/players', prefix: '/people/players' },
  { key: 'agents',  label: 'Agents',  icon: 'support_agent', to: '/people/agents',  prefix: '/people/agents' },
  { key: 'bots',    label: 'Bots',    icon: 'smart_toy',     to: '/people/bots',    prefix: '/people/bots' },
];

export default function PeopleSegmentBar({ current }: { current: Segment }) {
  const loc = useLocation();
  return (
    <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 pt-3 space-y-2">
      {/* Segment pills */}
      <div className="flex gap-1.5 overflow-x-auto pb-0.5
                      [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {SEGMENTS.map((s) => {
          const active = current === s.key || loc.pathname.startsWith(s.prefix);
          return (
            <Link
              key={s.key}
              to={s.to}
              className={`shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full
                          text-sm font-bold transition active:scale-[.98]
                          ${active
                            ? 'bg-sky-500 text-white shadow-[0_2px_10px_rgba(14,165,233,0.4)]'
                            : 'bg-white/60 dark:bg-arena-800/60 text-arena-700 dark:text-neon-200/70 ' +
                              'border border-arena-200/60 dark:border-arena-600/40 hover:border-sky-400/50'}`}
            >
              <Icon name={s.icon} size={16} filled /> {s.label}
            </Link>
          );
        })}
      </div>

      {/* Maintenance links — previously hidden (Unassigned players had NO menu
          entry; Bulk reassign lived under Settings ▸ Tools). Surfaced here so
          they live with the people they act on. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1">
        <Link to="/settings/reassign"
          className="inline-flex items-center gap-1 text-[12px] font-semibold
                     text-arena-500 dark:text-neon-200/60 hover:text-sky-600 dark:hover:text-sky-300 transition">
          <Icon name="swap_horiz" size={14} /> Bulk reassign
        </Link>
        <Link to="/settings/unassigned-players"
          className="inline-flex items-center gap-1 text-[12px] font-semibold
                     text-arena-500 dark:text-neon-200/60 hover:text-sky-600 dark:hover:text-sky-300 transition">
          <Icon name="person_off" size={14} /> Unassigned players
        </Link>
      </div>
    </div>
  );
}
